import React from 'react';
import {
  ChatComponent,
  CalendarSnippetData,
  ItineraryLinkData,
} from '../../services/chatApi';
import EventPreviewCard from './EventPreviewCard';
import ItineraryPreviewCard from './ItineraryPreviewCard';
import './ChatComponentRenderer.css';

interface DraftSelectionOptions {
  type?: string;
  message?: string;
  drafts?: Array<{
    id: number;
    title: string;
    dates: string;
    item_count: number;
  }>;
}

interface ChatComponentRendererProps {
  component: ChatComponent;
  onConfirmAction: (actionId: string) => void;
  onSelectOption?: (option: string) => void;
  onViewItinerary?: (id: number | string) => void;
}

const ChatComponentRenderer: React.FC<ChatComponentRendererProps> = ({
  component,
  onConfirmAction,
  onSelectOption,
  onViewItinerary,
}) => {
  switch (component.type) {
    case 'event_preview': {
      const data = component.data as React.ComponentProps<typeof EventPreviewCard>['event'];
      return (
        <EventPreviewCard
          event={data}
          actionId={component.action_id || ''}
          onConfirm={() => component.action_id && onConfirmAction(component.action_id)}
        />
      );
    }

    case 'itinerary_preview': {
      const data = component.data as React.ComponentProps<typeof ItineraryPreviewCard>['itinerary'] & { id?: number };
      return (
        <ItineraryPreviewCard
          itinerary={data}
          actionId={component.action_id || ''}
          onConfirm={() => component.action_id && onConfirmAction(component.action_id)}
          onViewFull={data.id && onViewItinerary ? () => onViewItinerary(data.id!) : undefined}
        />
      );
    }

    case 'itinerary_link': {
      const data = component.data as ItineraryLinkData;
      return (
        <ItineraryLinkCard
          id={data.id}
          title={data.title}
          message={data.message}
          onView={onViewItinerary}
        />
      );
    }

    case 'calendar_snippet': {
      const data = component.data as CalendarSnippetData;
      return (
        <CalendarSnippet events={data.events} />
      );
    }

    case 'options': {
      const data = component.data as DraftSelectionOptions | string[];
      return (
        <OptionButtons
          options={data}
          onSelect={onSelectOption}
        />
      );
    }

    default:
      return null;
  }
};

// Calendar Snippet Component
const CalendarSnippet: React.FC<{ events: CalendarSnippetData['events'] }> = ({ events }) => {
  if (!events || events.length === 0) {
    return (
      <div className="calendar-snippet empty">
        No events found.
      </div>
    );
  }

  return (
    <div className="calendar-snippet">
      {events.map((event, idx) => (
        <div key={idx} className="snippet-event">
          <div className="snippet-event-time">
            <span className="snippet-time">{event.time}</span>
          </div>
          <div className="snippet-event-details">
            <span className="snippet-title">{event.title}</span>
            {event.location && (
              <span className="snippet-location">{event.location}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Option Buttons Component
interface OptionButtonsProps {
  options: DraftSelectionOptions | string[];
  onSelect?: (option: string) => void;
}

const OptionButtons: React.FC<OptionButtonsProps> = ({ options, onSelect }) => {
  // Handle simple string options (suggestions)
  if (Array.isArray(options)) {
    return (
      <div className="option-buttons simple-options">
        {options.map((opt, idx) => (
          <button
            key={idx}
            className="option-btn"
            onClick={() => onSelect?.(opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }

  // Handle draft selection options
  if (options.type === 'draft_selection' && options.drafts) {
    return (
      <div className="option-buttons">
        {options.message && (
          <div className="options-message">{options.message}</div>
        )}
        <div className="draft-options">
          {options.drafts.map((draft) => (
            <button
              key={draft.id}
              className="draft-option-btn"
              onClick={() => onSelect?.(`continue_draft:${draft.id}`)}
            >
              <span className="draft-title">{draft.title}</span>
              <span className="draft-meta">
                {draft.dates} ({draft.item_count} items)
              </span>
            </button>
          ))}
          <button
            className="start-fresh-btn"
            onClick={() => onSelect?.('start_fresh')}
          >
            Start Fresh
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// Itinerary Link Card Component - shows after itinerary is created
interface ItineraryLinkCardProps {
  id: number | string;
  title: string;
  message?: string;
  onView?: (id: number | string) => void;
}

const ItineraryLinkCard: React.FC<ItineraryLinkCardProps> = ({
  id,
  title,
  message,
  onView,
}) => {
  return (
    <div className="itinerary-link-card">
      <div className="itinerary-link-icon">&#x1F5D3;</div>
      <div className="itinerary-link-content">
        <h4 className="itinerary-link-title">{title}</h4>
        {message && <p className="itinerary-link-message">{message}</p>}
      </div>
      {onView && (
        <button
          className="itinerary-link-btn"
          onClick={() => onView(id)}
        >
          View Itinerary
        </button>
      )}
    </div>
  );
};

export default ChatComponentRenderer;
